/*
  SharpPixel — comportements de la landing page.

  Deux composants, aucune dependance : le thème charge ce fichier en defer
  depuis les sections qui en ont besoin.

  Principe de base : tout ce qui est indispensable à l'achat fonctionne déjà
  sans ce fichier. Le formulaire poste nativement, et le changement de coloris
  est piloté en CSS par :has(). Ce qui suit n'ajoute que du confort :
  lecture vidéo maîtrisée, paramètre d'URL, préchargement, barre collante.
*/
(() => {
  'use strict';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
    Vidéo de la page.

    Deux modes, portés par l'attribut data-mode :
    - "hero"   : lecture au chargement, bouton pause/lecture toujours visible.
    - "inview" : preload="none", lecture à l'entrée dans l'écran et pause à la
                 sortie, pour ne pas télécharger ni animer hors de vue.

    Dans les deux cas, prefers-reduced-motion coupe la lecture automatique.
    La vidéo reste accessible : le bouton permet de la lancer à la demande.
  */
  class SharppixelVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-sharppixel-video-toggle]');
      if (!this.video) return;

      this.mode = this.dataset.mode || 'inview';

      // Révèle la vidéo seulement quand elle a des images à montrer : avant,
      // c'est le poster en <img> qui occupe la place, et c'est lui le LCP.
      this.video.addEventListener('playing', () => this.classList.add('is-playing'));

      if (this.toggle) {
        this.toggle.addEventListener('click', () => this.#toggle());
        this.toggle.hidden = false;
      }

      if (this.mode === 'inview') {
        this.#observe();
      } else if (!reducedMotion()) {
        this.#play();
      } else {
        this.#reflect(false);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    #observe() {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            // Une pause manuelle doit tenir : on ne relance pas au scroll
            // ce que la personne vient d'arrêter elle-même.
            if (entry.isIntersecting && !this.paused && !reducedMotion()) this.#play();
            else if (!entry.isIntersecting) this.video.pause();
          }
        },
        { threshold: 0.25 }
      );
      this.observer.observe(this);
    }

    #play() {
      // Safari refuse la lecture si le muet n'est pas explicite côté JS.
      this.video.muted = true;
      const attempt = this.video.play();
      if (attempt) attempt.then(() => this.#reflect(true)).catch(() => this.#reflect(false));
    }

    #toggle() {
      if (this.video.paused) {
        this.paused = false;
        this.#play();
      } else {
        this.paused = true;
        this.video.pause();
        this.#reflect(false);
      }
    }

    #reflect(playing) {
      if (!this.toggle) return;
      const label = playing ? this.toggle.dataset.labelPause : this.toggle.dataset.labelPlay;
      this.toggle.setAttribute('aria-label', label);
      this.toggle.dataset.state = playing ? 'playing' : 'paused';
    }
  }

  /*
    Sélecteur de coloris.

    Le changement d'affichage est fait en CSS : le groupe radio pilote les
    panneaux via :has(). L'état d'achat (libellé, rupture, prix) est rendu
    côté serveur, un jeu par coloris. Ce composant ne s'occupe donc que de ce
    que ni le CSS ni Liquid ne peuvent faire.

    1. le paramètre ?coloris=, lu à l'arrivée et tenu à jour ensuite, pour les
       liens de stories et de publicités ;
    2. le préchargement du média au survol ou au focus d'une pastille ;
    3. data-product-id sur <product-form-component>, que le thème renvoie dans
       l'événement cart:update ;
    4. la barre d'achat collante, qui n'existe que si JS tourne.
  */
  class SharppixelSelector extends HTMLElement {
    connectedCallback() {
      // La barre collante sert aussi sur une fiche produit, qui n'a qu'un
      // seul coloris et donc aucun groupe radio : on l'installe avant de
      // décider s'il y a des pastilles à câbler.
      this.sticky = this.querySelector('[data-sharppixel-sticky]');
      this.#stickyBar();

      this.radios = Array.from(this.querySelectorAll('[data-coloris-radio]'));
      if (!this.radios.length) return;

      this.productForm = this.querySelector('product-form-component');
      this.preloaded = new Set();

      for (const radio of this.radios) {
        radio.addEventListener('change', () => this.#select(radio, true));
        radio.addEventListener('focus', () => this.#preload(radio));

        const label = this.querySelector(`[for="${radio.id}"]`);
        if (label) {
          label.addEventListener('pointerenter', () => this.#preload(radio));
          label.addEventListener('touchstart', () => this.#preload(radio), { passive: true });
        }
      }

      this.#applyUrl();
      this.#select(this.radios.find((radio) => radio.checked) || this.radios[0], false);
    }

    disconnectedCallback() {
      this.stickyObserver?.disconnect();
    }

    // Un lien de story ou de publicité arrive avec ?coloris=rouge : la page
    // doit s'ouvrir directement sur le bon cadran.
    #applyUrl() {
      // Sur une fiche produit, la page EST déjà un coloris : laisser le
      // paramètre agir ouvrirait la fiche Rainbow sur le Rouge, avec un
      // titre qui contredit le contenu.
      if (this.dataset.fixedColorway === 'true') return;

      const wanted = new URLSearchParams(window.location.search).get('coloris');
      if (!wanted) return;

      const match = this.radios.find((radio) => radio.dataset.slug === wanted.toLowerCase());
      if (match) match.checked = true;
    }

    #select(radio, fromUser) {
      if (!radio) return;
      radio.checked = true;

      // Le thème renvoie cet identifiant dans cart:update. Sans mise à jour,
      // les cinq coloris seraient rapportés sous le produit du premier bloc.
      if (this.productForm && radio.dataset.productId) {
        this.productForm.dataset.productId = radio.dataset.productId;
      }

      if (fromUser) this.#writeUrl(radio);
    }

    #writeUrl(radio) {
      if (!radio.dataset.slug) return;

      const url = new URL(window.location.href);
      url.searchParams.set('coloris', radio.dataset.slug);
      window.history.replaceState({}, '', url);
    }

    // Au survol d'une pastille, on va chercher l'image du panneau visé pour
    // que la bascule soit instantanée. Une seule fois par coloris.
    #preload(radio) {
      const source = radio.dataset.preload;
      if (!source || this.preloaded.has(source)) return;

      this.preloaded.add(source);
      const image = new Image();
      image.src = source;
    }

    // La barre apparaît quand le sélecteur sort de l'écran : le bouton reste
    // alors à portée de pouce pendant la lecture du reste de la page.
    #stickyBar() {
      if (!this.sticky) return;

      const anchor = this.querySelector('[data-sharppixel-sticky-anchor]');
      if (!anchor) return;

      this.sticky.hidden = false;
      this.stickyObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.sticky.dataset.state = entry.isIntersecting ? 'hidden' : 'visible';
          }
        },
        { threshold: 0 }
      );
      this.stickyObserver.observe(anchor);
    }
  }

  if (!customElements.get('sharppixel-video')) {
    customElements.define('sharppixel-video', SharppixelVideo);
  }
  if (!customElements.get('sharppixel-selector')) {
    customElements.define('sharppixel-selector', SharppixelSelector);
  }
})();
